/**
 * Same-page invoice file save. A cross-origin signed URL ignores the download
 * attribute and navigates the current tab, so the bytes are fetched first and
 * saved from a blob URL.
 */

export function invoiceDocumentDownloadFilename(filename: string | null | undefined): string {
  const base = (filename ?? "").replace(/^.*[/\\]/, "").trim();
  return base.length > 0 ? base : "vendor-invoice";
}

async function fetchInvoiceDocumentBlob(url: string): Promise<Blob> {
  const response = await fetch(url, { cache: "no-store", mode: "cors" });
  if (!response.ok) {
    throw new Error(`Download failed (${response.status}).`);
  }
  return response.blob();
}

function saveBlobOnThisPage(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = filename;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
}

export async function downloadInvoiceDocumentFile(
  input: {
    file?: Blob | null;
    url?: string | null;
    filename?: string | null;
  },
  hooks?: {
    fetchFile?: (url: string) => Promise<Blob>;
    saveBlob?: (blob: Blob, filename: string) => void;
  },
): Promise<void> {
  const filename = invoiceDocumentDownloadFilename(
    input.filename ?? (input.file instanceof File ? input.file.name : null),
  );
  let blob = input.file ?? null;
  if (blob == null) {
    const url = input.url?.trim() ?? "";
    if (url === "") return;
    const fetchFile = hooks?.fetchFile ?? fetchInvoiceDocumentBlob;
    blob = await fetchFile(url);
  }
  const saveBlob = hooks?.saveBlob ?? saveBlobOnThisPage;
  saveBlob(blob, filename);
}
